import { useEffect, useState } from 'react';
import {
  importerDepuisTrello,
  importerCarteTrello,
  reimporterCarteTrello,
} from '../services/contactService';
import { getListesTrello } from '../services/trelloService';
import SelectRecherche from '../components/SelectRecherche';
import { STATUTS_DEMANDE, STATUT_GENERANT } from './statutsDemande';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import '../assets/styles/Parametrage.css';

// Liste et règle communes à l'import et à la saisie manuelle d'une
// demande : les deux posent la même question (cf. statutsDemande.js).
const STATUTS = STATUTS_DEMANDE;

/**
 * Import d'une liste Trello : on choisit l'état dans lequel les demandes
 * entrent, et si les fichiers agrafés aux cartes rejoignent le dossier.
 *
 * Le même écran depuis Demande de devis et depuis Demande de devis > Trello :
 * la liste est toujours proposée au choix ; `liste`, quand elle est donnée,
 * n'est que la présélection (celle dont on vient).
 *
 * Avec `carte`, c'est cette seule carte qui entre : plus de liste à choisir,
 * les mêmes questions (état, pièces jointes) et le même compte rendu.
 *
 * Avec `reimport`, la carte est déjà entrée : on la relit dans Trello et on
 * reporte ce qui a changé sur la demande. Plus d'état à choisir (il ne bouge
 * pas), pas de devis généré — seulement les pièces jointes, et un compte
 * rendu des champs repris.
 */
function ImportTrelloPanel({ liste = null, carte = null, reimport = false, onClose, onImporte }) {
  const uneCarte = Boolean(carte);
  const [statut, setStatut] = useState(STATUT_GENERANT);
  const [avecPiecesJointes, setAvecPiecesJointes] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState(null);
  const progression = useProgression(enCours, { estimeeMs: uneCarte ? 10000 : 30000 });

  // On fait choisir la liste plutôt que de retomber sur ID_LISTDEVIS : cette
  // variable peut désigner une liste qui n'existe plus.
  const [listes, setListes] = useState([]);
  const [listeChoisie, setListeChoisie] = useState(liste ? String(liste.id) : '');

  useEffect(() => {
    // Une carte seule : la liste n'a pas à être choisie, ni chargée.
    if (carte) return undefined;
    let vivant = true;
    getListesTrello()
      .then((res) => {
        if (!vivant) return;
        setListes(res.data.listes || []);
        // Sans présélection, la liste configurée côté serveur, si elle existe encore.
        if (!liste && res.data.listeDevis) {
          setListeChoisie((courante) => courante || res.data.listeDevis);
        }
      })
      .catch((err) => vivant && setErreur(err.message || 'Listes Trello illisibles.'));
    return () => {
      vivant = false;
    };
  }, [liste, carte]);

  const choisi = STATUTS.find((s) => s.valeur === statut);

  async function lancer() {
    setEnCours(true);
    setErreur(null);
    try {
      let res;
      if (reimport) res = await reimporterCarteTrello(carte.id, { avecPiecesJointes });
      else if (carte) res = await importerCarteTrello(carte.id, { statut, avecPiecesJointes });
      else res = await importerDepuisTrello(listeChoisie, { statut, avecPiecesJointes });
      setResultat(res.data);
      const aChange = res.data?.importes > 0 || res.data?.misAJour > 0;
      if (aChange && onImporte) onImporte(res.data);
    } catch (err) {
      setErreur(err.message || 'Import Trello impossible.');
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="param-modal-overlay" onClick={enCours ? undefined : onClose}>
      <div className="param-modal" onClick={(e) => e.stopPropagation()}>
        <div className="param-modal-header">
          <h2 className="param-modal-title" style={{ whiteSpace: 'pre-line' }}>
            {reimport ? 'Re-importer la carte' : uneCarte ? 'Importer la carte' : 'Importer depuis Trello'}
          </h2>
          <button type="button" className="param-modal-close" onClick={onClose} disabled={enCours}>
            ×
          </button>
        </div>

        <div className="param-modal-body param-form">
          {!resultat && (
            <>
              {uneCarte && (
                <div className="param-form-message">
                  <strong>{carte.nom}</strong>
                  <div className="devis-status-detail" style={{ marginTop: 4 }}>
                    {reimport
                      ? 'Les champs renseignés dans la carte remplacent ceux de la demande. Statut inchangé, aucun devis généré.'
                      : 'Cette carte entre comme demande de devis ; l’IA complète ce qui manque.'}
                  </div>
                </div>
              )}
              {!uneCarte && (
              <label className="param-field">
                <span className="param-label">Liste Trello à importer</span>
                <SelectRecherche
                  className="param-select"
                  value={listeChoisie}
                  onChange={(e) => setListeChoisie(e.target.value)}
                  disabled={enCours || (listes.length === 0 && !liste)}
                >
                  <option value="">Choisir une liste…</option>
                  {/* Tant que les listes chargent, la présélection reste lisible. */}
                  {listes.length === 0 && liste && (
                    <option value={liste.id}>{liste.nom.replace(/\s+/g, ' ')}</option>
                  )}
                  {listes.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.nom.replace(/\s+/g, ' ')}
                      {l.cartes !== undefined ? ` (${l.importees} / ${l.cartes})` : ''}
                    </option>
                  ))}
                </SelectRecherche>
              </label>
              )}

              {!reimport && (
              <label className="param-field">
                <span className="param-label">
                  {uneCarte ? 'État de la demande importée' : 'État des demandes importées'}
                </span>
                <SelectRecherche
                  className="param-select"
                  value={statut}
                  onChange={(e) => setStatut(e.target.value)}
                  disabled={enCours}
                >
                  {STATUTS.map((s) => (
                    <option key={s.valeur} value={s.valeur}>
                      {s.label} — {s.note}
                    </option>
                  ))}
                </SelectRecherche>
              </label>
              )}

              {!reimport && (
              <div className="param-form-message">
                {statut === STATUT_GENERANT ? (
                  <>
                    {uneCarte ? 'La demande importée enchaînera' : 'Chaque demande importée enchaînera'}{' '}
                    sur la <strong>rédaction automatique de son devis</strong>.
                  </>
                ) : (
                  <>
                    <strong>Aucun devis ne sera généré</strong> : il existe déjà pour ces affaires.
                  </>
                )}
              </div>
              )}

              <label className="param-checkbox-field">
                <input
                  type="checkbox"
                  checked={avecPiecesJointes}
                  onChange={(e) => setAvecPiecesJointes(e.target.checked)}
                  disabled={enCours}
                />
                <span>
                  {uneCarte
                    ? 'Rattacher les fichiers agrafés à la carte (PDF, Word) au dossier de la demande'
                    : 'Rattacher les fichiers agrafés aux cartes (PDF, Word) au dossier de la demande'}
                </span>
              </label>

              {erreur && (
                <div className="param-form-message param-form-message-error">{erreur}</div>
              )}
            </>
          )}

          {resultat && (
            <>
              <div
                className={`param-form-message${
                  resultat.importes > 0 || resultat.misAJour > 0 ? ' param-form-message-success' : ''
                }`}
              >
                {reimport ? (
                  resultat.misAJour > 0 ? (
                    <>
                      <strong>Demande mise à jour</strong> depuis la carte :{' '}
                      {resultat.champsModifies.join(', ')}.
                      {resultat.devisExistants > 0 && (
                        <>
                          {' '}
                          Vérifiez le devis avant envoi : il reprend ces coordonnées.
                        </>
                      )}
                    </>
                  ) : (
                    'Rien à reporter : la carte dit la même chose que la demande.'
                  )
                ) : resultat.importes > 0 ? (
                  <>
                    <strong>
                      {resultat.importes} demande{resultat.importes > 1 ? 's' : ''}
                    </strong>{' '}
                    importée{resultat.importes > 1 ? 's' : ''} en «&nbsp;{choisi?.label}&nbsp;».
                    {resultat.genererDevis
                      ? ' Leur devis est en cours de rédaction.'
                      : ' Aucun devis généré, comme prévu pour cet état.'}
                  </>
                ) : uneCarte && resultat.doublons > 0 ? (
                  'Cette carte est déjà entrée comme demande de devis.'
                ) : uneCarte ? (
                  'La carte n’a pas pu être importée : voir le détail ci-dessous.'
                ) : (
                  'Aucune nouvelle demande : rien d’exploitable dans cette liste.'
                )}
              </div>

              <table className="param-table">
                <tbody>
                  {!uneCarte && (
                    <tr>
                      <td>Cartes lues</td>
                      <td>
                        <strong>{resultat.total}</strong>
                      </td>
                    </tr>
                  )}
                  {!reimport && (
                  <tr>
                    <td>{uneCarte ? 'Importée' : 'Importées'}</td>
                    <td>
                      <strong>{resultat.importes}</strong>
                    </td>
                  </tr>
                  )}
                  {reimport && (
                    <tr>
                      <td>Champs repris de la carte</td>
                      <td>
                        <strong>{resultat.champsModifies?.length || 0}</strong>
                      </td>
                    </tr>
                  )}
                  {!uneCarte && (
                    <tr>
                      <td>Déjà connues</td>
                      <td>{resultat.doublons}</td>
                    </tr>
                  )}
                  {resultat.sansCoordonnees > 0 && (
                    <tr>
                      <td>Sans nom, e-mail ni société (importées, à compléter)</td>
                      <td>{resultat.sansCoordonnees}</td>
                    </tr>
                  )}
                  {resultat.pieces && (
                    <>
                      <tr>
                        <td>Pièces rattachées</td>
                        <td>
                          <strong>{resultat.pieces.importees}</strong>
                        </td>
                      </tr>
                      {resultat.pieces.doublons > 0 && (
                        <tr>
                          <td>Pièces déjà présentes</td>
                          <td>{resultat.pieces.doublons}</td>
                        </tr>
                      )}
                      {resultat.pieces.ignorees > 0 && (
                        <tr>
                          <td>Pièces au format non pris en charge</td>
                          <td>{resultat.pieces.ignorees}</td>
                        </tr>
                      )}
                      {resultat.pieces.echecs > 0 && (
                        <tr>
                          <td>Pièces en échec</td>
                          <td>{resultat.pieces.echecs}</td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>

              {resultat.pieces?.details?.length > 0 && (
                <div className="param-form-message param-form-message-error">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {resultat.pieces.details.slice(0, 8).map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
              )}

              {resultat.erreurs?.length > 0 && (
                <div className="param-form-message param-form-message-error">
                  {resultat.erreurs.length} carte{resultat.erreurs.length > 1 ? 's' : ''} en
                  erreur :
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {resultat.erreurs.slice(0, 5).map((e) => (
                      <li key={e.carteId}>{e.message}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          <BarreProgression
            valeur={progression}
            libelle={reimport ? 'Ré-import de la carte' : 'Import depuis Trello'}
            estimation
          />
          <div className="param-form-actions">
            <button type="button" className="param-btn" onClick={onClose} disabled={enCours}>
              {resultat ? 'Fermer' : 'Annuler'}
            </button>
            {!resultat && (
              <button
                type="button"
                className="param-btn param-btn-primary"
                onClick={lancer}
                disabled={enCours || (!uneCarte && !listeChoisie)}
              >
                {enCours
                  ? reimport ? 'Ré-import en cours' : 'Import en cours'
                  : reimport ? 'Re-importer' : 'Lancer l’import'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImportTrelloPanel;
